<template>
  <div v-if="entry" class="slide-page">
    <div class="slide-layout">
      <div class="slide-main">
        <SlideChrome
          :title="entry.title"
          :index="index"
          :total="slides.length"
        />
        <SlideMarkdown :source="entry.body" />
      </div>
      <aside v-if="entry.poll" class="slide-side">
        <PollPanel :slide-id="entry.id" :poll="entry.poll" />
        <AdminPollResults
          v-if="showAdminPoll"
          class="admin-panel"
          :slide-id="entry.id"
          :poll="entry.poll"
        />
      </aside>
    </div>

    <nav class="slide-footer" aria-label="Slide navigation">
      <button class="btn btn-secondary" :disabled="!prevId" @click="go(prevId)">
        ← Prev
      </button>
      <JourneyTrack :slide-id="entry.id" />
      <button class="btn btn-primary" :disabled="!nextId" @click="go(nextId)">
        Next →
      </button>
    </nav>
  </div>
  <div v-else class="muted">Slide not found.</div>
</template>

<script setup>
import { computed, watch, onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { slides, getSlide, getSlideIndex } from '../content/deck'
import { putProgress, checkAdmin, getMe } from '../services/api'
import SlideChrome from '../components/SlideChrome.vue'
import SlideMarkdown from '../content/deck/SlideMarkdown.vue'
import PollPanel from '../components/PollPanel.vue'
import AdminPollResults from '../components/AdminPollResults.vue'
import JourneyTrack from '../components/JourneyTrack.vue'

const route = useRoute()
const router = useRouter()
const isAdmin = ref(false)

const entry = computed(() => getSlide(route.params.slideId))
const index = computed(() => getSlideIndex(route.params.slideId))
const prevId = computed(() => (index.value > 0 ? slides[index.value - 1].id : null))
const nextId = computed(() =>
  index.value >= 0 && index.value < slides.length - 1 ? slides[index.value + 1].id : null,
)
const showAdminPoll = computed(() => isAdmin.value && !!entry.value?.poll)

function go(id) {
  if (id) router.push(`/slides/${id}`)
}

function onKey(e) {
  if (e.target.matches('input, textarea, select, [contenteditable]')) return
  if (e.key === 'ArrowLeft') go(prevId.value)
  if (e.key === 'ArrowRight') go(nextId.value)
}

async function saveProgress(id) {
  if (!id) return
  try {
    await putProgress(id)
  } catch {
    /* non-blocking */
  }
}

onMounted(async () => {
  window.addEventListener('keydown', onKey)
  try {
    const res = await checkAdmin()
    isAdmin.value = !!res.isAdmin
  } catch {
    isAdmin.value = false
  }
  if (!isAdmin.value) {
    try {
      const me = await getMe()
      isAdmin.value = !!me.isAdmin
    } catch {
      /* keep false */
    }
  }
  saveProgress(route.params.slideId)
})

onUnmounted(() => {
  window.removeEventListener('keydown', onKey)
})

watch(
  () => route.params.slideId,
  (id) => saveProgress(id),
)
</script>

<style scoped>
.slide-page {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - var(--nav-height) - 3rem);
}

.slide-layout {
  display: grid;
  grid-template-columns: 1fr minmax(260px, 320px);
  gap: 1.5rem;
  align-items: start;
  flex: 1;
  padding-bottom: 6.5rem;
}

.slide-side {
  position: sticky;
  top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.slide-footer {
  position: sticky;
  bottom: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin: 0 -1.25rem -1.5rem;
  padding: 0.65rem 1.25rem 0.75rem;
  background: var(--cream);
  border-top: 1px solid var(--border);
  box-shadow: 0 -6px 20px rgba(11, 31, 58, 0.06);
}

.footer-progress {
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: var(--muted);
}

@media (max-width: 860px) {
  .slide-layout {
    grid-template-columns: 1fr;
  }

  .slide-side {
    position: static;
  }
}
</style>
